import React, { ComponentProps, useState } from 'react';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { ContextMenu, ContextMenuButton, ContextMenuContainer } from '../../../_lib/components/ContextMenu';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { RssFeedCreateOrEditForm } from '../partials/RssFeedCreateOrEditForm';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { SonForm } from '../../../_lib/Form2';
import { RssFeedDeleteForm } from '../partials/RssFeedDeleteForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useRefreshBlockInDefaultView } from '../../../_lib/BlockFetcher';
import { useDelayed } from '../../../hooks/useDelayed';

type RssFeedEditableItem = {
  title: string;
  id: string;
  edit: RssFeedCreateOrEditForm;
  delete: SonForm<any>;
};

type RssFeedEdit = {
  create: RssFeedCreateOrEditForm;
  items: RssFeedEditableItem[];
};

function RssFeedEdit({ create, items }: RssFeedEdit) {
  const { t } = useSonTranslation('RssFeed');

  const [activeForm, setActiveForm] = useState<{
    type: 'add' | 'edit' | 'delete';
    form: RssFeedCreateOrEditForm | RssFeedDeleteForm;
  }>();

  const { refresh } = useRefreshBlockInDefaultView();

  const closeModal = () => setActiveForm(undefined);
  const delayedRefresh = useDelayed(refresh);

  const onSuccess = () => {
    delayedRefresh();
  };

  const switchActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} initialFocusIndex={1} headline={t('editView.delete.headline')}>
            <RssFeedDeleteForm form={activeForm.form} onSuccess={onSuccess} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} initialFocusIndex={1} headline={t('editView.createOrEdit.addHeadline')}>
            <RssFeedCreateOrEditForm form={activeForm.form} onSuccess={onSuccess} />
          </ModalDialog>
        );
      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} initialFocusIndex={1} headline={t('editView.createOrEdit.editHeadline')}>
            <RssFeedCreateOrEditForm form={activeForm.form} onSuccess={onSuccess} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  const contextMenu = (item: RssFeedEditableItem) => {
    return (
      <ContextMenu tooltipText={t('editView.manageFeed', { item: item.title })}>
        <ContextMenuButton onClick={() => setActiveForm({ type: 'edit', form: item.edit })}>
          {t('editView.editFeed')}
        </ContextMenuButton>
        <ContextMenuButton onClick={() => setActiveForm({ type: 'delete', form: item.delete })}>
          {t('editView.deleteFeed')}
        </ContextMenuButton>
      </ContextMenu>
    );
  };

  return (
    <>
      <BlockBar
        heading={t('editView.addFeed')}
        hierarchy={3}
        modifier={items.length > 0 ? ['border-bottom', 'border-top'] : 'border-top'}
      >
        <BlockBarButton
          onClick={() => setActiveForm({ type: 'add', form: create })}
          text={t('editView.addFeed')}
          svgSpriteId={Icons.ADD_PLUS}
        />
      </BlockBar>
      <Heading hierarchy={4} className="invisible">
        {t('editView.headline')}
      </Heading>
      <ContentList>
        {items.map(item => (
          <ContentListItem key={item.id}>
            <ContextMenuContainer contextMenu={contextMenu(item)}>
              <div className="font-regular-15-19">{item.title}</div>
            </ContextMenuContainer>
          </ContentListItem>
        ))}
      </ContentList>
      {switchActiveForm()}
    </>
  );
}

export const RssFeedEditView = asFetchableView(RssFeedEdit);
export type RssFeedEditView = ComponentProps<typeof RssFeedEditView>;
